import { apiClient } from '@dukene/shared';

export interface ThemeAsset {
  id: string;
  key: string;
  content?: string | null;
  attachmentUrl?: string | null;
  contentType: string;
  sectionSchema?: any;
  isCustomized?: boolean;
  updatedAt?: string;
}

export type ThemeAssetKind =
  | 'section'
  | 'snippet'
  | 'template'
  | 'layout'
  | 'asset'
  | 'config';

export interface StorePageTemplate {
  id: string;
  templateType: string;
  sectionsData?: any;
}

export interface StoreTheme {
  id: string;
  installedVersion: string;
  role: 'MAIN' | 'UNPUBLISHED' | 'DEVELOPMENT';
  settingsData?: Record<string, any> | null;
  updateAvailable?: boolean;
  theme: {
    id: string;
    name: string;
    description?: string | null;
    previewImage?: string | null;
    currentVersion: string;
    author?: { firstName?: string | null; lastName?: string | null };
  };
  assets?: ThemeAsset[];
  pageTemplates?: StorePageTemplate[];
  _count?: { assets: number };
}

export interface MarketplaceTheme {
  id: string;
  name: string;
  description?: string | null;
  previewImage?: string | null;
  currentVersion: string;
  isSystemTheme: boolean;
  price: string | number;
  tags: string[];
}

const merchantThemeBase = '/merchant/themes';

export const themeApi = {
  listInstalled: () => apiClient.get<StoreTheme[]>(merchantThemeBase),
  getInstalled: (storeThemeId: string) =>
    apiClient.get<StoreTheme>(`${merchantThemeBase}/${storeThemeId}`),
  listMarketplace: () =>
    apiClient.get<MarketplaceTheme[]>('/marketplace/themes'),
  install: (themeId: string) =>
    apiClient.post<{ storeThemeId: string }>(
      `${merchantThemeBase}/${themeId}/install`,
    ),
  createAsset: (
    storeThemeId: string,
    input: {
      kind: ThemeAssetKind;
      name: string;
      content?: string;
      contentType?: string;
    },
  ) =>
    apiClient.post<ThemeAsset>(
      `${merchantThemeBase}/${storeThemeId}/assets`,
      input,
    ),
  updateAsset: (storeThemeId: string, key: string, content: string) =>
    apiClient.put<ThemeAsset>(
      `${merchantThemeBase}/${storeThemeId}/assets/${encodeURIComponent(key)}`,
      { content },
    ),
  deleteAsset: (storeThemeId: string, key: string) =>
    apiClient.delete<null>(
      `${merchantThemeBase}/${storeThemeId}/assets/${encodeURIComponent(key)}`,
    ),
  saveSettings: (storeThemeId: string, settingsData: Record<string, any>) =>
    apiClient.put<StoreTheme>(`${merchantThemeBase}/${storeThemeId}/settings`, {
      settingsData,
    }),
  saveTemplate: (
    storeThemeId: string,
    templateType: string,
    sectionsData: Record<string, any>,
  ) =>
    apiClient.put<StorePageTemplate>(
      `${merchantThemeBase}/${storeThemeId}/templates/${templateType}`,
      { sectionsData },
    ),
  publish: (storeThemeId: string) =>
    apiClient.put<null>(`${merchantThemeBase}/${storeThemeId}/publish`),
  previewUrl: (storeThemeId: string, templateType = 'index') => {
    const params = new URLSearchParams({ template: templateType });
    if (typeof window !== 'undefined') {
      const token = apiClient.getAccessToken();
      const storeId = window.localStorage.getItem('activeStoreId');
      if (token) params.set('token', token);
      if (storeId) params.set('storeId', storeId);
    }
    const baseURL = apiClient.getClient().defaults.baseURL || '/api/v1';
    return `${baseURL.replace(/\/$/, '')}/merchant/themes/${storeThemeId}/preview?${params.toString()}`;
  },
  generateAiTheme: (brandAssets: Record<string, string>) =>
    apiClient.post<{ themeId: string }>('/merchant/ai/generate-theme', { brandAssets }),
  deleteTheme: (storeThemeId: string) =>
    apiClient.delete<null>(`${merchantThemeBase}/${storeThemeId}`),
};
